// @flow
import React, { useEffect, useMemo } from 'react'
import type { Match } from 'react-router-dom'
import type { ID, ResourcesList, TrackingSchema, Asset } from 'app/core/types'
import history from 'app/main/routerHistory'
import { Autocomplete } from 'app/components/Form'
import pipe from 'app/core/utils/pipe'
import type { Pipe } from 'app/core/utils/pipeNext.type'
import { getResources } from 'app/store/selectors'
import { documentTitle } from 'app/components/DocumentTitle/DocumentTitle.jsx'

import { useDispatch, useSelector } from 'react-redux'
import classes from './RightContent.module.scss'
import { sortTrackingSchemasOptions } from './utils.jsx'
import TableFollowUpRedux from './TableFollowUpRedux.tsx'
import { resetReduxFilters } from '../../../../store/reducers/tablesFilters.ts'
import { resetFollowUpAssets } from '../../../../store/reducers/assets.ts'
import { setAssetId, setTrackingSchemaID } from '../../../../store/reducers/tableFollowUpPrefs.ts'
import { setStatsOpen } from '../../../../store/reducers/stats.ts'
import { setCustomTableSetting, setTableId, setTablePage } from '../../../../store/reducers/tablesPrefs.ts'

type Props = {|
  match: Match,
  trackingSchemas: ResourcesList<TrackingSchema>,
  trackingSchemaId: ?ID,
  asset: Asset,
  resetTable: boolean,
  permissions?: FollowUpPermissions,
  lockFilters?: boolean,
  tableId?: string,
  trackingSchemaId?: string,
|}

function RightContentView(props: Props): React$Node {
  const { trackingSchemaId = '', asset, trackingSchemas, match, permissions, lockFilters, tableId } = props
  const dispatch = useDispatch()

  const currentTrackingSchema = trackingSchemaId ? trackingSchemas[trackingSchemaId] : null
  const projectId = match.params.projectId || ''

  const options = sortTrackingSchemasOptions(trackingSchemas, asset.assetType)
  const selected = options.find((option) => option.value === trackingSchemaId) ? trackingSchemaId : null
  const customTableSetting = useSelector((state: RootStateOrAny) => state.tablesPrefs.customTableSetting)

  function onChangeTrackingSchema({ value }: Object, e: SyntheticMouseEvent<>) {
    if (!lockFilters && !customTableSetting) {
      dispatch(resetReduxFilters())
    }
    dispatch(setStatsOpen(false))
    dispatch(setCustomTableSetting(null))
    dispatch(setTrackingSchemaID(trackingSchemaId))
    dispatch(resetFollowUpAssets())
    dispatch(setTablePage?.(0))
    history.pushWithParams(props.match, { trackingSchemaId: value }, e.shiftKey ? '?resetTable=true' : undefined)
  }

  function toolbar(selected, options) {
    return (
      <div className={classes.toolbarContainer}>
        <div className={classes.toolbarContainer}>
          <Autocomplete
            selected={selected}
            options={options}
            onChange={onChangeTrackingSchema}
            style={{ marginRight: 5, height: 35, width: 250 }}
            inputStyle={{ backgroundColor: '#fecd00' }}
            animeKeyPressed={{ shift: true }}
          />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (trackingSchemaId) {
      let title = trackingSchemas[trackingSchemaId]?.name || 'Follow-up'
      if (asset?.name) title = `${asset.name} - ${title}`
      documentTitle(title, 'page')
    }
  }, [asset, trackingSchemaId])

  useEffect(() => {
    dispatch(setTableId(tableId))
    dispatch(setTrackingSchemaID(trackingSchemaId))
    dispatch(setAssetId(asset?.id))
  }, [tableId, trackingSchemaId, asset?.id])

  return (
    <TableFollowUpRedux
      asset={asset}
      trackingSchema={currentTrackingSchema}
      extendToolbar={toolbar(selected, options)}
      projectId={projectId}
      permissions={permissions}
      lockFilters={lockFilters}
      tableId={tableId}
    />
  )
}

const pipeInst: Pipe<{ isExternal?: boolean }, typeof RightContentView> = pipe()

export const RightContentContainer: React$ComponentType<any> = pipeInst
  .withRouter()
  .connect((state, props) => {
    const { isExternal } = props
    const { projectId, assetId } = props.match.params
    const resetTable = new URLSearchParams(window.location.search).get('resetTable') === 'true'

    const trackingSchemas = getResources(state, 'trackingSchemas', (ts) => {
      if (ts.isVisible && ts.project === projectId) {
        if (!isExternal && !ts.isExternal) return true
        if (isExternal && ts.isExternal) return true
        return false
      }
      return false
    })

    return {
      projectId,
      trackingSchemas,
      asset: getResources(state, 'assets', assetId),
      resetTable,
    }
  })
  .isLoading((props) => !props.asset)
  .render(({ projectId, history, isExternal, location, ...props }) => <RightContentView {...props} />)
